// src/pages/safety/Checklist.jsx
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { listSafetyChecklists, saveSafetyDoc } from "../../api/safety";
import { softDeleteDoc } from "../../api/papers";
import SidePanel from "../../components/common/layout/SidePanel";
import ReusableCalendar, {
  buildCounts,
} from "../../components/common/feature/Calendar";
import ChecklistView from "../../components/safety/view/ChecklistView";
import ChecklistEditor from "../../components/safety/editor/ChecklistEditor";
import SafetySignalLight from "../../components/widgets/SafetySignalLight";
import PageHeader from "../../components/common/ui/PageHeader";

// ─────────────────────────────────────────────
// 스타일 유틸 (색상/배지/이벤트 카드)
// ─────────────────────────────────────────────
const dotHex = (c) =>
  c === "blue"
    ? "#2563eb"
    : c === "green"
    ? "#16a34a"
    : c === "yellow"
    ? "#f59e0b"
    : c === "red"
    ? "#ef4444"
    : "#9ca3af";

const StatusPill = ({ label, color }) => {
  const base =
    "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1";
  const theme = {
    blue: "bg-blue-50 text-blue-700 ring-blue-200",
    green: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    yellow: "bg-amber-50 text-amber-700 ring-amber-200",
    red: "bg-rose-50 text-rose-700 ring-rose-200",
    gray: "bg-gray-50 text-gray-600 ring-gray-200",
  }[color || "gray"];
  return (
    <span className={`${base} ${theme}`}>
      <span className="mr-1.5" style={{ color: dotHex(color) }}>
        ●
      </span>
      {label}
    </span>
  );
};

const EventRow = ({ e, onOpen }) => (
  <li className="group flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3 hover:border-gray-300 hover:shadow-sm transition">
    <span
      className="text-lg"
      title={e.unified_status}
      style={{ color: dotHex(e._color) }}
    >
      ●
    </span>
    <div className="min-w-0 flex-1">
      <div className="flex items-center justify-between gap-3">
        <p className="truncate font-medium text-gray-900">
          {e.title || "(제목 없음)"}
        </p>
        <StatusPill label={e.unified_status} color={e._color} />
      </div>
      <p className="mt-1 line-clamp-1 text-sm text-gray-500">
        <b>점검자</b> {e.inspector || "-"} · <b>확인자</b> {e.confirmer || "-"}
      </p>
    </div>
    <button
      onClick={onOpen}
      className="ml-auto shrink-0 rounded-lg border px-2.5 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
    >
      상세
    </button>
  </li>
);

const fmtDay = (d) => format(d, "yyyy-MM-dd");
const dayFromStr = (s) => {
  if (!s) return "";
  if (typeof s === "string") return s.slice(0, 10);
  if (s instanceof Date) return format(s, "yyyy-MM-dd");
  return "";
};

// 상태 정규화: 띄어쓰기/동의어 보정
const normalizeStatus = (raw) => {
  const v = String(raw || "").trim();
  if (v === "진행중") return "진행 중";
  if (v === "조치필요") return "조치 필요";
  return v;
};

const progressToColor = (p) => {
  switch (p) {
    case "completed":
      return "green";
    case "planned":
    case "in_progress":
    case "action_needed":
      return "yellow";
    case "failed":
    case "urgent":
      return "red";
    case "cancelled":
      return "gray";
    default:
      return "gray";
  }
};

const progressLabel = (p) => {
  switch (p) {
    case "completed":
      return "적합(완료)";
    case "planned":
      return "예정";
    case "in_progress":
      return "진행 중";
    case "action_needed":
      return "조치 필요";
    case "failed":
      return "부적합";
    case "urgent":
      return "긴급";
    case "cancelled":
      return "취소";
    default:
      return "미정";
  }
};

// 5색 집계(blue/green/yellow/red/gray)
const buildStatusCounts5 = (
  items = [],
  getDate = (x) => x?.date,
  getColor = () => "yellow"
) => {
  const out = {};
  for (const it of items) {
    const raw = getDate(it);
    const ymd =
      typeof raw === "string"
        ? raw.slice(0, 10)
        : raw instanceof Date
        ? format(raw, "yyyy-MM-dd")
        : "";
    if (!ymd) continue;
    const c = getColor(it); // 'blue' | 'green' | 'yellow' | 'red' | 'gray'
    const bucket = (out[ymd] ||= {
      blue: 0,
      green: 0,
      yellow: 0,
      red: 0,
      gray: 0,
      total: 0,
    });
    if (c === "blue") bucket.blue += 1;
    else if (c === "green") bucket.green += 1;
    else if (c === "red") bucket.red += 1;
    else if (c === "gray") bucket.gray += 1;
    else bucket.yellow += 1;
    bucket.total += 1;
  }
  return out;
};

// ─────────────────────────────────────────────
// 컴포넌트
// ─────────────────────────────────────────────
const Checklist = () => {
  const navigate = useNavigate();
  const projectId = localStorage.getItem("projectId");

  // 선택 날짜
  const [selectedDate, setSelectedDate] = useState(new Date());

  // 데이터 / 상태
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  // 우측 폼
  const [saving, setSaving] = useState(false);

  // 상세보기
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [detailDoc, setDetailDoc] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editPayload, setEditPayload] = useState(null);

  // 목록 불러오기 — “저장된 구조” 기준 파싱 (doc_date 통일 + 색상은 status 기반)
  const fetchChecklist = async ({ year, month } = {}) => {
    try {
      setLoading(true);

      const params = { project: projectId };
      if (year && month) {
        params.year = year;
        params.month = month;
      }

      const data = await listSafetyChecklists(params); // ✅ 백엔드도 year/month 지원
      const raw = data?.results || data?.data?.results || data;

      const list = (Array.isArray(raw) ? raw : []).map((row) => {
        const progress = row.progress || "planned";
        const check_date = row.check_date || null;
        const result_date = row.result_date || null;
        const doc_date = row.doc_date || result_date || check_date;
        const unified_label = progress || "미정";
        const color = row.color || progressToColor(progress) || "gray";
        const docDateStr =
          typeof doc_date === "string"
            ? doc_date.slice(0, 10)
            : doc_date
            ? format(new Date(doc_date), "yyyy-MM-dd")
            : null;

        return {
          ...row,
          title: row.title || "(제목 없음)",
          inspector: row.inspector || "",
          confirmer: row.confirmer || "",
          unified_status: unified_label,
          _color: color,
          doc_date: docDateStr,
        };
      });

      setEvents(list);
    } catch (err) {
      console.error("목록 불러오기 실패:", err);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchChecklist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 캘린더 집계
  const countsByDate = useMemo(
    () => buildCounts(events, (e) => e?.doc_date),
    [events]
  );

  const statusCountsByDate = useMemo(
    () =>
      buildStatusCounts5(
        events,
        (e) => e?.doc_date,
        (e) => e?._color || "gray"
      ),
    [events]
  );

  // 선택 날짜의 이벤트
  const selectedEvents = useMemo(
    () =>
      events.filter((e) => dayFromStr(e?.doc_date) === fmtDay(selectedDate)),
    [events, selectedDate]
  );

  // // 저장(우측 폼)
  // const handleSave = async () => {
  //   try {
  //     if (!formPayload) return;
  //     const projectId = localStorage.getItem("projectId");
  //     if (!projectId) return setError("프로젝트가 선택되지 않았습니다.");

  //     if (!formPayload.title) return setError("제목을 입력하세요.");
  //     const meta = formPayload.meta || {};
  //     if (!meta.check_date) return setError("점검일을 선택하세요.");

  //     setSaving(true);
  //     setError("");

  //     const progress = meta.progress || meta.status || "예정";
  //     const approvalMode = formPayload.approval === true;
  //     const saveStatus = approvalMode ? "submit" : "none";

  //     const statusStd = normalizeStatus(progress);
  //     const docDateForSave = ["적합", "부적합", "긴급", "진행 중"].includes(
  //       statusStd
  //     )
  //       ? meta.result_date || meta.check_date
  //       : meta.check_date;

  //     const res = await saveSafetyDoc({
  //       project: projectId,
  //       module: "safety",
  //       doc_type: formPayload.doc_type,
  //       title: formPayload.title,
  //       status: saveStatus,
  //       meta: {
  //         ...meta,
  //         progress,
  //       },
  //     });

  //     const savedDns = res?.dns;
  //     if (!savedDns) {
  //       await fetchChecklist();
  //       setShowForm(false);
  //       setFormPayload(null);
  //       alert("저장은 되었지만 문서 ID를 찾지 못했습니다.");
  //       return;
  //     }

  //     await fetchChecklist();
  //     setShowForm(false);
  //     setFormPayload(null);
  //   } catch (e) {
  //     console.error("저장 실패:", e.response?.data || e.message);
  //     setError(
  //       e?.response?.data?.detail ||
  //         e?.response?.data?.error ||
  //         "저장 중 오류가 발생했습니다."
  //     );
  //   } finally {
  //     setSaving(false);
  //   }
  // };

  // 상세 열기
  const openDetail = (dns, docType) => {
    const project = projectId;
    navigate(`/project/${project}/safety/${docType}/${dns}/view`);
  };
  // const openDetail = async (dns) => {
  //   setDetailOpen(true);
  //   setEditMode(false);
  //   setEditPayload(null);
  //   setDetailLoading(true);
  //   setDetailError("");
  //   setDetailDoc(null);
  //   try {
  //     const tx = await getSafetyDocDetail(dns, { project: projectId });

  //     const meta = tx?.meta || {};
  //     const progress = meta.progress || "미정";
  //     const label = progress.trim() || "미정";

  //     const mergedValues = {
  //       title: tx.title || meta.title || "",
  //       doc_type: tx.doc_type || "plan",
  //       check_type: meta.check_type || "",
  //       check_date: meta.check_date || "",
  //       result_date: meta.result_date || "",
  //       overall_result: meta.overall_result || "",
  //       memo: meta.memo || "",
  //       progress: progress,
  //       items: meta.items || [],
  //       scar_actions: meta.scar_actions || [],
  //       scar_problem: meta.scar_problem || "",
  //       scar_immediate: meta.scar_immediate || "",
  //       scar_root_cause: meta.scar_root_cause || "",
  //       photos_meta: meta.photos_meta || [],
  //     };

  //     setDetailDoc({
  //       id: tx.dns,
  //       dns: tx.dns,
  //       form_type: "ISC",
  //       title: tx.title || mergedValues.title || "(제목 없음)",
  //       updated_at: tx.updated_at || mergedValues.check_date || null,
  //       meta: mergedValues,
  //       unified_status: progress,
  //     });
  //   } catch (e) {
  //     setDetailError(e?.response?.data?.detail || "불러오기 실패");
  //   } finally {
  //     setDetailLoading(false);
  //   }
  // };
  const closeDetail = () => setDetailOpen(false);

  // 수정 저장
  const handleUpdate = async () => {
    if (!detailDoc?.id) return;
    if (!editPayload) {
      alert("변경된 내용이 없습니다.");
      return;
    }
    try {
      setSaving(true);

      const v = editPayload;
      const check_date = v.check_date ?? detailDoc.meta?.check_date ?? null;
      const result_date = v.result_date ?? detailDoc.meta?.result_date ?? null;

      const values = {
        type: "standard",
        project: projectId,
        form_type: (detailDoc.form_type || "ISC").toUpperCase(),
        title: v.title ?? detailDoc.values?.title ?? "",
        memo: v.memo ?? detailDoc.meta?.memo ?? "",
        check_type: v.check_type || v.type || detailDoc.meta?.check_type,
        inspector: v.inspector || detailDoc.values?.inspector || "",
        confirmer: v.confirmer || detailDoc.values?.confirmer || "",
        visibility: v.visibility || detailDoc.values?.visibility || "public",
        referees: v.referees || detailDoc.values?.referees || [],
        items: Array.isArray(v.items)
          ? v.items
          : Array.isArray(detailDoc.meta?.items)
          ? detailDoc.meta.items
          : [],
        check_date,
        result_date,
        status: normalizeStatus(
          v.status || detailDoc.values?.status || "임시저장"
        ),
        inspection_result:
          v.inspection_result ||
          detailDoc.values?.inspection_result ||
          (detailDoc.unified_status === "예정" ? "예정" : ""),
      };

      // 상태 기준 doc_date 재산출
      const statusStd2 = normalizeStatus(values.status ?? "");
      const newDocDate = ["적합(완료)", "부적합", "긴급", "진행 중"].includes(
        statusStd2 || "예정"
      )
        ? values.result_date || values.check_date
        : values.check_date;

      await saveSafetyDoc({
        project: projectId,
        form_type: "ISC",
        dns: detailDoc.dns ?? detailDoc.id,
        body_json: { layout: null, values },
        template_title: values.title,
        status: "draft",
        doc_date: newDocDate || null,
      });

      await openDetail(detailDoc.id, detailDoc.doc_type);
      await fetchChecklist({
        year: new Date(values.check_date).getFullYear(),
        month: new Date(values.check_date).getMonth() + 1,
      });
      setEditMode(false);
      setEditPayload(null);
    } catch (e) {
      alert(
        e?.response?.data?.detail ||
          e?.response?.data?.error ||
          "수정 저장 중 오류가 발생했습니다."
      );
    } finally {
      setSaving(false);
    }
  };

  // 리스트/아이콘 색상
  const colorToHex = (c) =>
    c === "green"
      ? "#16a34a"
      : c === "yellow"
      ? "#f59e0b"
      : c === "red"
      ? "#ef4444"
      : "#9ca3af";

  return (
    <div className="p-6">
      <PageHeader>안전 점검 일정</PageHeader>
      {/* 헤더 */}
      <div className="mb-2 flex items-center justify-between">
        {/* <div>
          <p className="mt-0.5 text-xs text-gray-500">
            프로젝트 <b>{projectId || "-"}</b> 기준 점검 캘린더입니다.
          </p>
        </div> */}
        <SafetySignalLight
          onLightClick={(status) => console.log("filter:", status)}
        />
      </div>

      {/* 레전드: 여백 축소 */}
      <div className=" flex justify-between mb-2">
        <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-gray-700">
          <StatusPill label="적합" color="green" />
          <StatusPill label="점검 예정 · 조치 필요 · 진행 중" color="yellow" />
          <StatusPill label="부적합 · 긴급" color="red" />
          <StatusPill label="취소" color="gray" />
        </div>

        <button
          onClick={() => {
            if (!projectId) return;
            navigate(`/project/${projectId}/safety/check/new`);
          }}
          className="btn-save"
        >
          일정 등록
        </button>
      </div>

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 lg:col-span-7 transition-all duration-500">
          <div className="rounded-xl border border-gray-200 bg-white p-3">
            <div className="overflow-hidden min-h-[560px]">
              <ReusableCalendar
                value={selectedDate}
                onChange={setSelectedDate}
                countsByDate={countsByDate}
                statusCountsByDate={statusCountsByDate}
                onMonthChange={(date) => {
                  const month = date.getMonth() + 1;
                  const year = date.getFullYear();
                  fetchChecklist({ year, month });
                }}
              />
            </div>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-5 transition-all duration-500">
          <div className="rounded-xl border border-gray-200 bg-white p-3">
            {/* {showForm ? (
              <div>
                <div className="mb-2.5 flex items-start justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">
                      점검 일정 등록
                    </h3>
                    <p className="mt-0.5 text-xs text-gray-500">
                      점검일과 상태를 입력하면 캘린더에 즉시 반영됩니다.
                    </p>
                  </div>
                  <div className="flex gap-1.5">
                    <button
                      onClick={() => setShowForm(false)}
                      className="rounded-md border px-2.5 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
                    >
                      취소
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="rounded-md bg-blue-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-60"
                    >
                      {saving ? "저장 중..." : "저장"}
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="mb-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                    {error}
                  </div>
                )}
                <ChecklistEditor
                  initial={emptyInitial}
                  readOnly={false}
                  onChange={setFormPayload}
                />
              </div>
            ) : (
              <>
                <div className="mb-2">
                  <h3 className="text-sm font-semibold text-gray-900">
                    {format(selectedDate, "yyyy-MM-dd")} 일정
                  </h3>
                  <p className="text-muted">
                    목록에서 항목을 선택하면 상세 페이지로 이동할 수 있어요.
                  </p>
                </div>

                {selectedEvents.map((e) => (
                  <div
                    key={e.dns}
                    onClick={() => openDetail(e.dns, e.doc_type)}
                    className="mb-2 rounded-lg border border-gray-200 bg-white p-3 shadow-sm cursor-pointer hover:bg-gray-50"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <p className="truncate text-sm font-semibold text-gray-900">
                        {e.title}
                      </p>
                      <StatusPill
                        label={progressLabel(e.progress)}
                        color={progressToColor(e.progress)}
                      />
                    </div>
                    <p className="mt-1 text-[11px] text-gray-500 break-all">
                      {" "}
                      <span className="font-medium text-gray-600">
                        문서번호
                      </span>{" "}
                      {e.dns}
                    </p>

                    <div className="mt-3 flex items-center justify-between text-xs text-gray-600">
                      {" "}
                      <div className="flex items-center gap-4">
                        {" "}
                        <span>
                          <span className="font-medium text-gray-700">
                            점검자
                          </span>{" "}
                          {e.inspector || "-"}
                        </span>
                        <span>
                          <span className="font-medium text-gray-700">
                            확인자
                          </span>{" "}
                          {e.confirmer || "-"}
                        </span>
                      </div>
                      <button
                        // onClick={() => openDetail(e.dns, e.doc_type)}
                        className="btn-save-sm"
                      >
                        상세보기
                      </button>
                    </div>
                  </div>
                ))}

                {selectedEvents.length === 0 && !loading && (
                  <div className="text-muted">
                    오늘은 일정이 없어요. 우측 상단의 <b>일정 등록</b> 버튼으로
                    추가하세요.
                  </div>
                )}
              </>
            )} */}

            {/* ✅ 우측 패널은 항상 일정 목록만 표시 */}
            <>
              <div className="mb-2">
                <h3 className="text-sm font-semibold text-gray-900">
                  {format(selectedDate, "yyyy-MM-dd")} 일정
                </h3>
                <p className="text-muted">
                  목록에서 항목을 선택하면 상세 페이지로 이동할 수 있어요.
                </p>
              </div>

              {selectedEvents.map((e) => (
                <div
                  key={e.dns}
                  onClick={() => openDetail(e.dns, e.doc_type)}
                  className="mb-2 rounded-lg border border-gray-200 bg-white p-3 shadow-sm cursor-pointer hover:bg-gray-50"
                >
                  <div className="flex items-start justify-between gap-3">
                    <p className="truncate text-sm font-semibold text-gray-900">
                      {e.title}
                    </p>
                    <StatusPill
                      label={progressLabel(e.progress)}
                      color={progressToColor(e.progress)}
                    />
                  </div>
                  <p className="mt-1 text-[11px] text-gray-500 break-all">
                    <span className="font-medium text-gray-600">문서번호</span>{" "}
                    {e.dns}
                  </p>

                  <div className="mt-3 flex items-center justify-between text-xs text-gray-600">
                    <div className="flex items-center gap-4">
                      <span>
                        <span className="font-medium text-gray-700">
                          점검자
                        </span>{" "}
                        {e.inspector || "-"}
                      </span>
                      <span>
                        <span className="font-medium text-gray-700">
                          확인자
                        </span>{" "}
                        {e.confirmer || "-"}
                      </span>
                    </div>
                    <button className="btn-save-sm">상세보기</button>
                  </div>
                </div>
              ))}

              {selectedEvents.length === 0 && !loading && (
                <div className="text-muted">
                  오늘은 일정이 없어요. 우측 상단의 <b>일정 등록</b> 버튼으로
                  추가하세요.
                </div>
              )}
            </>
          </div>
        </div>
      </div>

      {/* 상세 사이드 패널 (버튼만 컴팩트/색 변경) */}
      <SidePanel
        isOpen={detailOpen}
        onClose={closeDetail}
        title={detailDoc?.title || "문서 상세"}
        width={1200}
        side="right"
        headerActions={
          detailDoc?.id ? (
            <div className="flex items-center gap-1.5">
              {!editMode ? (
                <button
                  onClick={() => setEditMode(true)}
                  className="rounded-md border px-2.5 py-1.5 text-xs hover:bg-gray-50"
                >
                  수정
                </button>
              ) : (
                <>
                  <button
                    onClick={handleUpdate}
                    disabled={saving}
                    className="rounded-md bg-blue-600 px-2.5 py-1.5 text-xs text-white disabled:opacity-60"
                  >
                    {saving ? "저장 중..." : "저장"}
                  </button>
                  <button
                    onClick={() => {
                      setEditMode(false);
                      setEditPayload(null);
                    }}
                    className="rounded-md border px-2.5 py-1.5 text-xs hover:bg-gray-50"
                  >
                    취소
                  </button>
                </>
              )}

              <button
                onClick={async () => {
                  if (!detailDoc?.id) return;
                  if (!window.confirm("이 문서를 삭제하시겠습니까?")) return;
                  try {
                    setDeleting(true);
                    await softDeleteDoc(detailDoc.id, true); // ✅ papers/docs/{dns}/soft-delete 호출
                    alert("문서가 삭제되었습니다.");
                    setDetailOpen(false); // 패널 닫기
                    await fetchChecklist(); // 목록 새로고침
                  } catch (e) {
                    console.error(e);
                    alert(`삭제 실패: ${e?.message || "알 수 없는 오류"}`);
                  } finally {
                    setDeleting(false);
                  }
                }}
                disabled={deleting}
                className="rounded-md border px-2.5 py-1.5 text-xs text-rose-600 hover:bg-rose-50 disabled:opacity-60"
              >
                {deleting ? "삭제 중..." : "삭제"}
              </button>

              <button
                onClick={() =>
                  navigate(
                    `/project/${projectId}/safety/checklist/boards/${detailDoc.id}/view`
                  )
                }
                className="rounded-md border px-2.5 py-1.5 text-xs hover:bg-gray-50"
              >
                전체 보기
              </button>
            </div>
          ) : null
        }
      >
        {detailLoading && (
          <div className="text-xs text-gray-500">불러오는 중…</div>
        )}
        {detailError && (
          <div className="text-xs text-rose-600">{detailError}</div>
        )}
        {!detailLoading && !detailError && detailDoc && (
          <div className="space-y-2">
            <div className="rounded-lg border bg-white p-3 text-xs text-gray-600">
              <div>
                유형: <b>{detailDoc.form_type}</b>
              </div>
              <div className="mt-0.5">
                진행상태:{" "}
                <StatusPill
                  label={progressLabel(detailDoc.unified_status)}
                  color={progressToColor(detailDoc.unified_status) || "gray"}
                />
              </div>
              {detailDoc.updated_at && (
                <div className="mt-0.5">
                  수정일: {new Date(detailDoc.updated_at).toLocaleString()}
                </div>
              )}
            </div>

            {!editMode ? (
              <ChecklistView doc={detailDoc} readOnly />
            ) : (
              <ChecklistEditor
                initial={detailDoc.meta}
                readOnly={false}
                onChange={setEditPayload}
                showPreview={false}
              />
            )}
          </div>
        )}
      </SidePanel>
    </div>
  );
};

export default Checklist;
